import { Link } from '@tanstack/react-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import { usePracticeAgents, usePrescreeningQueries } from '../api/hooks';
import type { PracticeAgentDevice, PrescreeningQuery } from '../api/types';
import { ConnectionBadge, lastImportText } from './practice-agents';
import {
  PRESCREENING_UNAVAILABLE,
  PrescreeningHeader,
  practiceName,
  prescreeningLoadError,
} from './prescreening';

export interface PracticeOverview {
  id: string;
  name: string | null;
  tenantName: string | null;
  teamName: string | null;
  /** The computer in use, or the last one revoked. */
  device: PracticeAgentDevice | null;
  queries: number;
}

/** A Practice's computer: the one not revoked, else the newest. */
export function practiceDevice(
  devices: PracticeAgentDevice[],
  practiceId: string
): PracticeAgentDevice | null {
  const own = devices
    .filter(device => device.practiceId === practiceId)
    .sort(
      (a, b) =>
        Number(a.connection === 'revoked') - Number(b.connection === 'revoked') ||
        b.enrolledAt.localeCompare(a.enrolledAt)
    );
  return own[0] ?? null;
}

/** Practices with a computer or a criteria query, by name. */
export function practiceOverviews(
  devices: PracticeAgentDevice[],
  queries: PrescreeningQuery[]
): PracticeOverview[] {
  const practices = new Map<string, PracticeOverview>();
  for (const device of devices) {
    if (practices.has(device.practiceId)) continue;
    const chosen = practiceDevice(devices, device.practiceId);
    practices.set(device.practiceId, {
      id: device.practiceId,
      name: chosen?.practiceName ?? null,
      tenantName: chosen?.tenantName ?? null,
      teamName: chosen?.teamName ?? null,
      device: chosen,
      queries: 0,
    });
  }
  for (const query of queries) {
    if (query.source !== 'practice' || !query.practiceId) continue;
    const known = practices.get(query.practiceId) ?? {
      id: query.practiceId,
      name: query.practiceName ?? null,
      tenantName: null,
      teamName: null,
      device: null,
      queries: 0,
    };
    known.name = known.name ?? query.practiceName ?? null;
    known.queries += 1;
    practices.set(query.practiceId, known);
  }
  return [...practices.values()].sort((a, b) =>
    practiceName(a.name).localeCompare(practiceName(b.name))
  );
}

export function PrescreeningPracticesPage() {
  const devices = usePracticeAgents(true);
  const queries = usePrescreeningQueries();
  const unavailable = devices.data === null && queries.data === null;
  const rows = practiceOverviews(devices.data ?? [], queries.data ?? []);

  return (
    <div>
      <PrescreeningHeader />
      <QueryState
        isLoading={devices.isLoading || queries.isLoading}
        error={prescreeningLoadError(devices.error ?? queries.error)}
      >
        {unavailable ? (
          <p role="status" className="text-sm text-muted-foreground">
            {PRESCREENING_UNAVAILABLE}
          </p>
        ) : (
          <>
            <p className="mb-4 max-w-2xl text-xs text-muted-foreground">
              Each Practice has its own lab code sets, which map a concept such as eGFR to the codes
              its labs use. Open a Practice to confirm them from its structure report.
            </p>
            <div className="overflow-x-auto rounded-3xl border">
              <Table aria-label="Practices">
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Practice computer</TableHead>
                    <TableHead>Last import</TableHead>
                    <TableHead className="text-right">Criteria queries</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(practice => (
                    <TableRow key={practice.id}>
                      <TableCell className="min-w-[12rem]">
                        <Link
                          to="/prescreening/practices/$practiceId"
                          params={{ practiceId: practice.id }}
                          className="font-medium underline-offset-4 hover:underline"
                        >
                          {practiceName(practice.name)}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs">
                        {[practice.tenantName, practice.teamName].filter(Boolean).join(' · ') ||
                          'Unknown'}
                      </TableCell>
                      <TableCell>
                        {practice.device ? (
                          <ConnectionBadge connection={practice.device.connection} />
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {practice.device
                          ? lastImportText(practice.device.lastHeartbeat)
                          : 'No import yet'}
                      </TableCell>
                      <TableCell className="text-right">{practice.queries}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        No Practice has a practice computer or a criteria query yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}
